(()=>{
"use strict";
const {compactResolver,normalizeZip,isContiguousState}=(()=>{
const CONTIGUOUS_STATES=Object.freeze('AL AZ AR CA CO CT DE DC FL GA ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(' '));
const isContiguousState=value=>CONTIGUOUS_STATES.includes(value);
const normalizeZip=value=>typeof value==='string'&&/^\d{5}$/.test(value.trim())?value.trim():null;
function compactResolver(data){
 if(data?.format!=='zip5-state2/v1'||typeof data.revision!=='string'||!data.revision||!Number.isSafeInteger(data.validFrom)
  ||!Number.isSafeInteger(data.validUntil)||data.validUntil<=data.validFrom||!Number.isSafeInteger(data.count)||data.count<1||data.count>100000
  ||typeof data.records!=='string'||data.records.length!==data.count*7)throw Error('INVALID_ZIP_DATA');
 const map=new Map();let previous='';
 for(let i=0;i<data.records.length;i+=7){const zip=data.records.slice(i,i+5),state=data.records.slice(i+5,i+7);
  if(!/^\d{5}$/.test(zip)||zip<=previous||!isContiguousState(state))throw Error('INVALID_ZIP_RECORD');
  map.set(zip,state);previous=zip;
 }
 return (value,now=Date.now())=>{const zip=normalizeZip(value);return Number.isSafeInteger(now)&&now>=data.validFrom&&now<data.validUntil&&map.has(zip)
  ?Object.freeze({zip,state:map.get(zip),countryCode:'US',revision:data.revision,expiresAt:data.validUntil}):null;};
}


return {compactResolver,normalizeZip,isContiguousState};
})();
const {validateShippingQuote,validateShippingCost}=(()=>{
/** Offline-only, fail-closed static shipping estimate predicate. No I/O or clock reads. */
const SUPPORTED_US_STATES = Object.freeze(('AL AZ AR CA CO CT DE FL GA ID IL IN IA KS KY LA ' +
  'ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC').split(' '));
const DEFAULT_POLICY = Object.freeze({
  quoteTtlMs: 120_000, maxClockSkewMs: 0,
});
const REQUEST_FIELDS = Object.freeze([
  'generation', 'variantId', 'quantity', 'countryCode', 'provinceCode', 'zip', 'currency',
]);
const object = x => x !== null && typeof x === 'object' && !Array.isArray(x);
const text = x => typeof x === 'string' && x.length > 0;
const compare = (a, b) => a < b ? -1 : a > b ? 1 : 0;
const integer = x => Number.isSafeInteger(x);
const owns = (x, k) => Object.prototype.hasOwnProperty.call(x, k);
const empty = x => Array.isArray(x) && x.length === 0;
const complete = c => object(c) && Array.isArray(c.nodes) && object(c.pageInfo)
  && c.pageInfo.hasNextPage === false && c.pageInfo.hasPreviousPage === false
  && owns(c.pageInfo, 'endCursor')
  && (c.nodes.length === 0 ? c.pageInfo.endCursor === null : text(c.pageInfo.endCursor));
const sameRequest = (a, b) => object(a) && object(b)
  && REQUEST_FIELDS.every(k => owns(a, k) && owns(b, k) && a[k] === b[k]);
const goodRequest = r => object(r) && integer(r.generation) && r.generation >= 1
  && typeof r.variantId === 'string' && /^gid:\/\/shopify\/ProductVariant\/[1-9]\d*$/.test(r.variantId)
  && integer(r.quantity) && r.quantity > 0 && r.quantity <= 2147483647 && r.countryCode === 'US' && r.currency === 'USD'
  && SUPPORTED_US_STATES.includes(r.provinceCode)
  && typeof r.zip === 'string' && /^\d{5}$/.test(r.zip);
const goodLine = (l, variant, quantity) => object(l) && text(l.id) && l.__typename === 'CartLine'
  && l.quantity === quantity && object(l.merchandise) && l.merchandise.__typename === 'ProductVariant'
  && l.merchandise.id === variant && owns(l, 'sellingPlanAllocation') && l.sellingPlanAllocation === null;
const stable = x => JSON.stringify(x, function (_, value) {
  return object(value) ? Object.fromEntries(Object.keys(value).sort().map(k => [k, value[k]])) : value;
});

/**
 * Trusted adapter contract is documented in README.md. All inputs must be inert JSON data.
 * A true result is a qualified partial-address static estimate, never postal validation.
 */
function assessShippingQuote(input, requireFree) {
  const reasons = new Set();
  const totals = {};
  const fail = reason => { reasons.add(reason); return false; };
  try {
    if (!object(input)) return { eligible: false, reasons: ['MALFORMED_INPUT'] };
    const { request, current, nowMs, lifecycle } = input;
    const policy = { ...DEFAULT_POLICY, ...(object(input.policy) ? input.policy : {}) };
    if (!goodRequest(request)) fail('INVALID_REQUEST');
    if (!goodRequest(current) || !sameRequest(request, current)) fail('STALE_REQUEST_GENERATION_OR_SELECTION');
    if (!integer(nowMs) || nowMs < 0) fail('INVALID_CLOCK');
    const configOk = ['quoteTtlMs'].every(k => integer(policy[k]) && policy[k] > 0)
      && integer(policy.maxClockSkewMs) && policy.maxClockSkewMs >= 0
      && policy.maxClockSkewMs <= policy.quoteTtlMs;
    if (!configOk) fail('INVALID_FRESHNESS_POLICY');
    const clockOk = t => integer(t) && t >= 0 && t <= nowMs + policy.maxClockSkewMs;
    if (!object(request) || !clockOk(request.startedAtMs)
      || nowMs - request.startedAtMs >= policy.quoteTtlMs) fail('EXPIRED_OR_INVALID_REQUEST_TIME');
    if (!goodRequest(request) || !configOk || !integer(nowMs)) {
      return { eligible: false, reasons: [...reasons].sort() };
    }

    // Require a complete, non-streaming response for every operation; no partial salvage.
    function stage(name, operation) {
      const s = object(lifecycle) ? lifecycle[name] : undefined;
      if (!object(s) || s.httpStatus !== 200 || s.complete !== true
        || !sameRequest(s.request, request) || !clockOk(s.receivedAtMs)
        || s.receivedAtMs < request.startedAtMs || !object(s.body)
        || !object(s.body.data) || owns(s.body, 'hasNext') || owns(s.body, 'incremental')
        || ['errors', 'warnings', 'userErrors'].some(k => owns(s.body, k) && !empty(s.body[k]))) {
        fail(`${name.toUpperCase()}_INCOMPLETE_OR_ERROR`);
        return null;
      }
      const body = operation ? s.body.data[operation] : s.body.data;
      if (!object(body) || (operation && (!empty(body.userErrors) || !empty(body.warnings)))
        || (!operation && ['errors', 'warnings', 'userErrors'].some(k => owns(body, k) && !empty(body[k])))
        || !object(body.cart)) {
        fail(`${name.toUpperCase()}_INCOMPLETE_OR_ERROR`);
        return null;
      }
      return body.cart;
    }
    const created = stage('create', 'cartCreate');
    const read = stage('read', null);
    const cleaned = stage('cleanup', 'cartLinesRemove');
    const carrierMode=object(lifecycle)&&owns(lifecycle,'carrierRead');
    const carrier=carrierMode?stage('carrierRead',null):null;
    const initial=carrierMode?stage('initialRead',null):null;
    if(carrierMode&&!(lifecycle.create?.receivedAtMs<=lifecycle.initialRead?.receivedAtMs
      &&lifecycle.initialRead?.receivedAtMs<=lifecycle.carrierRead?.receivedAtMs
      &&lifecycle.carrierRead?.receivedAtMs<=lifecycle.read?.receivedAtMs))fail('CARRIER_LIFECYCLE_ORDER_INVALID');
    if (object(lifecycle) && object(lifecycle.create) && object(lifecycle.read) && object(lifecycle.cleanup)
      && !(lifecycle.create.receivedAtMs <= lifecycle.read.receivedAtMs
        && lifecycle.read.receivedAtMs <= lifecycle.cleanup.receivedAtMs)) fail('LIFECYCLE_ORDER_INVALID');

    function checkCart(cart, label, quoteRequired=true) {
      if (!cart) return null;
      const bad = reason => fail(`${label}_${reason}`);
      if (!text(cart.id) || cart.totalQuantity !== request.quantity || !complete(cart.lines)
        || cart.lines.nodes.length !== 1 || !goodLine(cart.lines.nodes[0], request.variantId, request.quantity)) {
        bad('INVALID_CART_LINE_OR_PAGINATION');
        return null;
      }
      if (!object(cart.buyerIdentity) || cart.buyerIdentity.countryCode !== 'US') bad('BUYER_COUNTRY_MISMATCH');
      const addresses = object(cart.delivery) ? cart.delivery.addresses : undefined;
      const modern = Array.isArray(addresses) && addresses.length === 1 && object(addresses[0])
        && addresses[0].selected === true && addresses[0].oneTimeUse === true
        && object(addresses[0].address) && addresses[0].address.__typename === 'CartDeliveryAddress'
        && ['countryCode', 'provinceCode', 'zip'].every(k => addresses[0].address[k] === request[k]);
      if (!modern) bad('MODERN_SELECTED_ADDRESS_MISMATCH');
      if(!quoteRequired)return stable({id:cart.id,line:cart.lines.nodes[0],addresses,buyerIdentity:cart.buyerIdentity});
      if (!complete(cart.deliveryGroups) || cart.deliveryGroups.nodes.length === 0) {
        bad('EMPTY_OR_INCOMPLETE_DELIVERY_GROUPS');
        return null;
      }
      const line = cart.lines.nodes[0], groupIds = new Set(), coveredIds = new Set();
      let totalCents = 0;
      let coveredQuantity = 0;
      const fingerprints = [];
      for (const g of cart.deliveryGroups.nodes) {
        if (!object(g) || !text(g.id) || groupIds.has(g.id)) {
          bad('DUPLICATE_OR_MALFORMED_GROUP');
          continue;
        }
        groupIds.add(g.id);
        if (g.groupType !== 'ONE_TIME_PURCHASE') bad('NON_ONE_TIME_GROUP');
        if (!complete(g.cartLines) || g.cartLines.nodes.length === 0) {
          bad('EMPTY_OR_INCOMPLETE_GROUP_LINES');
        } else {
          for (const l of g.cartLines.nodes) {
            if (!goodLine(l, request.variantId, request.quantity) || l.id !== line.id || coveredIds.has(l.id)) {
              bad('GROUP_LINE_UNION_OR_QUANTITY_MISMATCH');
              continue;
            }
            coveredIds.add(l.id);
            coveredQuantity += l.quantity;
          }
        }
        const a = g.deliveryAddress;
        const canonical = object(a) && a.countryCodeV2 === request.countryCode && a.provinceCode === request.provinceCode;
        const observedLegacy = object(a) && a.countryCodeV2 === 'ZZ' && owns(a, 'provinceCode') && a.provinceCode === null
          && a.country === request.countryCode && a.province === request.provinceCode;
        if (!modern || !object(a) || a.zip !== request.zip || !(canonical || observedLegacy)) bad('GROUP_ADDRESS_MISMATCH');
        const handles = new Set();
        let zeroShipping = false, lowestCents = Infinity;
        if (!Array.isArray(g.deliveryOptions) || g.deliveryOptions.length === 0) bad('MISSING_DELIVERY_OPTIONS');
        else for (const o of g.deliveryOptions) {
          if (!object(o) || !text(o.handle) || handles.has(o.handle)
            || !['SHIPPING', 'PICK_UP'].includes(o.deliveryMethodType) || !object(o.estimatedCost)
            || typeof o.estimatedCost.amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(o.estimatedCost.amount)
            || o.estimatedCost.currencyCode !== 'USD') {
            bad('MALFORMED_DELIVERY_OPTION');
            continue;
          }
          handles.add(o.handle);
          const amount = o.estimatedCost.amount;
          if (!/^\d+(?:\.\d{1,2}0*)?$/.test(amount)) { bad('INVALID_CURRENCY_PRECISION'); continue; }
          const [whole, fraction = ''] = amount.split('.');
          const centsBig = BigInt(whole) * 100n + BigInt((fraction + '00').slice(0, 2));
          if (centsBig > BigInt(Number.MAX_SAFE_INTEGER)) { bad('INVALID_CURRENCY_AMOUNT'); continue; }
          const cents = Number(centsBig);
          if (o.deliveryMethodType === 'SHIPPING') {
            lowestCents = Math.min(lowestCents, cents);
            if (cents === 0) zeroShipping = true;
          }
        }
        if (requireFree && !zeroShipping) bad('NO_ZERO_USD_SHIPPING');
        if (!Number.isSafeInteger(lowestCents)) bad('NO_USD_SHIPPING');
        else totalCents += lowestCents;
        fingerprints.push(g);
      }
      if (!Number.isSafeInteger(totalCents)) bad('INVALID_TOTAL_AMOUNT');
      totals[label] = totalCents;
      if (coveredIds.size !== 1 || !coveredIds.has(line.id) || coveredQuantity !== request.quantity) bad('GROUP_LINE_UNION_OR_QUANTITY_MISMATCH');
      return stable({ id: cart.id, line, addresses, buyerIdentity: cart.buyerIdentity,
        groups: fingerprints.slice().sort((a, b) => compare(a.id, b.id)).map(g => ({ ...g,
          deliveryOptions: Array.isArray(g.deliveryOptions) ? g.deliveryOptions.slice().sort((a, b) => compare(String(a?.handle), String(b?.handle))) : g.deliveryOptions,
        })) });
    }
    const createFingerprint = checkCart(created, 'CREATE',!carrierMode);
    const readFingerprint = checkCart(read, 'READ');
    if(carrierMode){
      const initialContext=checkCart(initial,'INITIALREAD',false);
      const carrierContext=checkCart(carrier,'CARRIERCONTEXT',false);
      const readContext=checkCart(read,'READCONTEXT',false);
      const carrierFingerprint=checkCart(carrier,'CARRIERREAD');
      if(!createFingerprint||[initialContext,carrierContext,readContext].some(x=>x!==createFingerprint))fail('CARRIER_CART_CONTEXT_CHANGED');
      if(!carrierFingerprint||!readFingerprint||carrierFingerprint!==readFingerprint)fail('CARRIER_QUOTE_CHANGED_BETWEEN_READS');
    }else if (createFingerprint && readFingerprint && createFingerprint !== readFingerprint) fail('QUOTE_CHANGED_BETWEEN_CREATE_AND_READ');
    if (!cleaned || !created || !read || cleaned.id !== created.id || cleaned.id !== read.id
      || cleaned.totalQuantity !== 0 || !complete(cleaned.lines) || cleaned.lines.nodes.length !== 0) {
      fail('CLEANUP_NOT_CONFIRMED_EMPTY_FOR_SAME_CART');
    }
  } catch {
    fail('MALFORMED_INPUT');
  }
  const result = { eligible: reasons.size === 0, reasons: [...reasons].sort() };
  return !requireFree && result.eligible
    ? {...result, costCents: totals.READ, currency: 'USD', free: totals.READ === 0}
    : result;
}

function validateShippingQuote(input) { return assessShippingQuote(input, true); }
function validateShippingCost(input) { return assessShippingQuote(input, false); }

return {validateShippingQuote,validateShippingCost};
})();
const {QUERY}=(()=>{
const QUERY="mutation PdpQuoteCreate($input:CartInput!){cartCreate(input:$input){cart{...PdpQuoteCart}userErrors{code field message}warnings{code message}}}\nquery PdpQuoteRead($cartId:ID!){cart(id:$cartId){...PdpQuoteCart}}\nmutation PdpQuoteCleanup($cartId:ID!,$lineIds:[ID!]!){cartLinesRemove(cartId:$cartId,lineIds:$lineIds){cart{id totalQuantity lines(first:5){nodes{id quantity}pageInfo{hasNextPage hasPreviousPage endCursor}}}userErrors{code field message}warnings{code message}}}\nfragment PdpQuoteCart on Cart{id totalQuantity buyerIdentity{countryCode} delivery{addresses(selected:true){selected oneTimeUse address{__typename ... on CartDeliveryAddress{countryCode provinceCode zip}}}} lines(first:5){nodes{...PdpQuoteLine}pageInfo{hasNextPage hasPreviousPage endCursor}}deliveryGroups(first:10){nodes{id groupType deliveryAddress{country countryCodeV2 province provinceCode zip}cartLines(first:5){nodes{...PdpQuoteLine}pageInfo{hasNextPage hasPreviousPage endCursor}}deliveryOptions{handle deliveryMethodType estimatedCost{amount currencyCode}}}pageInfo{hasNextPage hasPreviousPage endCursor}}}\nfragment PdpQuoteLine on BaseCartLine{__typename id quantity merchandise{__typename ... on ProductVariant{id}}sellingPlanAllocation{__typename}}\n";


return {QUERY};
})();
const {CARRIER_QUERY,parseCarrierBody}=(()=>{
const CARRIER_QUERY=`query PdpCarrierRead($cartId:ID!){cart(id:$cartId){id totalQuantity buyerIdentity{countryCode}delivery{addresses(selected:true){selected oneTimeUse address{__typename ... on CartDeliveryAddress{countryCode provinceCode zip}}}}lines(first:5){nodes{...PdpCarrierLine}pageInfo{hasNextPage hasPreviousPage endCursor}}...PdpCarrierGroups @defer(label:"ShippingRates")}}
fragment PdpCarrierGroups on Cart{deliveryGroups(first:10,withCarrierRates:true){nodes{id groupType deliveryAddress{country countryCodeV2 province provinceCode zip}cartLines(first:5){nodes{...PdpCarrierLine}pageInfo{hasNextPage hasPreviousPage endCursor}}deliveryOptions{handle deliveryMethodType estimatedCost{amount currencyCode}}}pageInfo{hasNextPage hasPreviousPage endCursor}}}
fragment PdpCarrierLine on BaseCartLine{__typename id quantity merchandise{__typename ... on ProductVariant{id}}sellingPlanAllocation{__typename}}`;
function parseCarrierBody(raw, contentType) {
 const object=x=>x!==null&&typeof x==='object'&&!Array.isArray(x);
 const noErrors=x=>object(x)&&['errors','warnings','userErrors'].every(k=>!(k in x)||Array.isArray(x[k])&&x[k].length===0);
 if(typeof raw!=='string'||raw.length>1000000)throw Error('CARRIER_RESPONSE_INVALID');
 if(/^application\/(?:graphql-response\+)?json\b/i.test(contentType||'')){
  const body=JSON.parse(raw);
  if(!noErrors(body)||!object(body.data?.cart)||'incremental' in body||('hasNext'in body&&body.hasNext!==false))throw Error('CARRIER_RESPONSE_INCOMPLETE');
  delete body.hasNext;return body;
 }
 const match=/^multipart\/mixed\s*;\s*boundary=(?:"([A-Za-z0-9_-]{1,70})"|([A-Za-z0-9_-]{1,70}))(?:\s*;.*)?$/i.exec(contentType||'');
 if(!match)throw Error('CARRIER_CONTENT_TYPE_INVALID');
 const chunks=raw.split('--'+(match[1]||match[2]));
 if(chunks.shift().trim()!==''||chunks.pop().trim()!=='--'||chunks.length<2||chunks.length>4)throw Error('CARRIER_STREAM_INCOMPLETE');
 const parts=chunks.map(chunk=>{
  const split=/\r?\n\r?\n/.exec(chunk);if(!split)throw Error('CARRIER_PART_INVALID');
  const headers=chunk.slice(0,split.index);
  if(!/(?:^|\r?\n)content-type:\s*application\/json\s*(?:\r?\n|$)/i.test(headers))throw Error('CARRIER_PART_TYPE_INVALID');
  const p=JSON.parse(chunk.slice(split.index+split[0].length).trim());
  if(!noErrors(p))throw Error('CARRIER_PART_ERRORS');return p;
 });
 if(parts.some((p,i)=>p.hasNext!==(i<parts.length-1)))throw Error('CARRIER_STREAM_NOT_FINAL');
 const first=parts[0];
 if(!object(first.data?.cart)||'incremental'in first||'deliveryGroups'in first.data.cart)throw Error('CARRIER_BASE_INVALID');
 let groups;
 for(const part of parts.slice(1)){
  if('data'in part)throw Error('CARRIER_DUPLICATE_BASE');
  if(!Array.isArray(part.incremental)){
   if(part.hasNext===false&&Object.keys(part).every(k=>k==='hasNext'||k==='extensions'))continue;
   throw Error('CARRIER_PATCH_MISSING');
  }
  for(const patch of part.incremental){
   if(!noErrors(patch)||JSON.stringify(patch.path)!=='["cart"]'||('label'in patch&&patch.label!=='ShippingRates')
    ||!object(patch.data)||Object.keys(patch.data).length!==1||!object(patch.data.deliveryGroups)||groups)throw Error('CARRIER_PATCH_INVALID');
   groups=patch.data.deliveryGroups;
  }
 }
 if(!groups)throw Error('CARRIER_GROUPS_MISSING');
 return{data:{cart:{...first.data.cart,deliveryGroups:groups}}};
}

return {CARRIER_QUERY,parseCarrierBody};
})();
const {makeTransport}=(()=>{
const ENDPOINT='https://jdmuscleusa.myshopify.com/api/2026-10/graphql.json';
const QUOTE_TTL_MS=120000;
const object=x=>x!==null&&typeof x==='object'&&!Array.isArray(x);
const cleanErrors=b=>object(b)&&!['errors','warnings','userErrors'].some(k=>k in b&&(!Array.isArray(b[k])||b[k].length));
const complete=c=>object(c)&&Array.isArray(c.nodes)&&c.pageInfo?.hasNextPage===false&&c.pageInfo?.hasPreviousPage===false;
function rawRequest(request){return Object.freeze({generation:request.generation,variantId:'gid://shopify/ProductVariant/'+request.variantId,
 quantity:request.quantity,countryCode:'US',provinceCode:request.state,zip:request.zip,currency:'USD',startedAtMs:request.startedAt});}
function makeTransport({fetchFn=fetch,clock=()=>Date.now(),onSummary=()=>{}}={}) {
  const receipts=new WeakSet();
  async function operation(operationName,variables,request){
    const begin=clock();let response;
    try {response=await fetchFn(ENDPOINT,{method:'POST',credentials:'omit',redirect:'error',cache:'no-store',
      headers:{'Content-Type':'application/json','Accept':operationName==='PdpCarrierRead'?'multipart/mixed, application/json':'application/json'},body:JSON.stringify({query:operationName==='PdpCarrierRead'?CARRIER_QUERY:QUERY,operationName,variables})});}
    catch {throw Error('QUOTE_NETWORK_UNAVAILABLE');}
    let body=null;
    if(response.status===200&&operationName==='PdpCarrierRead'){
      try{body=parseCarrierBody(await response.text(),response.headers.get('content-type'));}catch{}
    }else if(response.status===200&&/^application\/(?:graphql-response\+)?json\b/i.test(response.headers.get('content-type')||'')){
      try{body=await response.json();}catch{}
    }
    return {request,httpStatus:response.status,complete:object(body),body,receivedAtMs:clock(),durationMs:clock()-begin};
  }
  async function cleanup(receipt){
    if(!receipts.has(receipt)||!receipt.cartId)return false;
    let cart=receipt.latestCart;
    for(let attempt=0;attempt<2;attempt++){
      try{
        if(!complete(cart?.lines)||cart.id!==receipt.cartId){const read=await operation('PdpQuoteRead',{cartId:receipt.cartId},receipt.request);cart=read.body?.data?.cart;if(!cleanErrors(read.body))return false;}
        if(!complete(cart?.lines)||cart.id!==receipt.cartId)return false;
        const ids=cart.lines.nodes.map(x=>x.id);
        if(ids.some(x=>typeof x!=='string'||!x)||new Set(ids).size!==ids.length)return false;
        if(ids.length===0)return cart.totalQuantity===0;
        const stage=await operation('PdpQuoteCleanup',{cartId:receipt.cartId,lineIds:ids},receipt.request);
        receipt.lifecycle.cleanup=stage;
        const payload=stage.body?.data?.cartLinesRemove,c=payload?.cart;
        if(stage.httpStatus===200&&stage.complete&&cleanErrors(stage.body)&&cleanErrors(payload)&&Array.isArray(payload.userErrors)&&Array.isArray(payload.warnings)
          &&c?.id===receipt.cartId&&c.totalQuantity===0&&complete(c.lines)&&c.lines.nodes.length===0)return true;
        cart=null;
      }catch{cart=null;}
    }
    return false;
  }
  return {
    async create(request){
      const key=rawRequest(request),destination={countryCode:'US',provinceCode:key.provinceCode,zip:key.zip};
      const stage=await operation('PdpQuoteCreate',{input:{buyerIdentity:{countryCode:'US'},lines:[{merchandiseId:key.variantId,quantity:key.quantity}],
        delivery:{addresses:[{address:{deliveryAddress:destination},selected:true,oneTimeUse:true,validationStrategy:'COUNTRY_CODE_ONLY'}]}}},key);
      const cart=stage.body?.data?.cartCreate?.cart;
      if(typeof cart?.id!=='string'||!cart.id)throw Error('QUOTE_CREATE_UNCONFIRMED');
      const receipt={cartId:cart.id,request:key,lifecycle:{create:stage},latestCart:cart,cleanupConfirmed:false};receipts.add(receipt);return receipt;
    },
    async read(receipt){
      if(!receipts.has(receipt))throw Error('UNOWNED_RECEIPT');
      const stage=await operation('PdpQuoteRead',{cartId:receipt.cartId},receipt.request);receipt.lifecycle.read=stage;
      if(stage.body?.data?.cart?.id===receipt.cartId)receipt.latestCart=stage.body.data.cart;
      const cart=stage.body?.data?.cart,groups=cart?.deliveryGroups;
      const staticFree=complete(groups)&&groups.nodes.length>0&&groups.nodes.every(g=>Array.isArray(g.deliveryOptions)&&g.deliveryOptions.some(o=>o.deliveryMethodType==='SHIPPING'&&o.estimatedCost?.currencyCode==='USD'&&/^0+(?:\.0+)?$/.test(o.estimatedCost.amount)));
      if(stage.httpStatus===200&&stage.complete&&cleanErrors(stage.body)&&cart?.id===receipt.cartId&&!staticFree){
        receipt.lifecycle.initialRead=stage;
        receipt.lifecycle.carrierRead=await operation('PdpCarrierRead',{cartId:receipt.cartId},receipt.request);
        const first=receipt.lifecycle.carrierRead.body?.data?.cart;
        if(first?.id===receipt.cartId)receipt.latestCart=first;
        if(!first)throw Error('CARRIER_QUOTE_UNAVAILABLE');
        receipt.lifecycle.read=await operation('PdpCarrierRead',{cartId:receipt.cartId},receipt.request);
        if(receipt.lifecycle.read.body?.data?.cart?.id===receipt.cartId)receipt.latestCart=receipt.lifecycle.read.body.data.cart;
      }
      return receipt;
    },
    async cleanup(receipt){
      const ok=await cleanup(receipt);receipt.cleanupConfirmed=ok;
      // Emit only allowlisted non-identifying diagnostics; never raw errors or cart IDs.
      const summary={cleanupConfirmed:ok,elapsedMs:clock()-receipt.request.startedAtMs,
        timings:Object.fromEntries(Object.entries(receipt.lifecycle).map(([k,s])=>[k,s.durationMs])),
        rates:(receipt.lifecycle.read?.body?.data?.cart?.deliveryGroups?.nodes||[]).map(g=>(g.deliveryOptions||[]).map(o=>({method:o.deliveryMethodType,amount:o.estimatedCost?.amount,currency:o.estimatedCost?.currencyCode}))),
        verdict:validateShippingCost({request:receipt.request,current:receipt.request,nowMs:clock(),lifecycle:receipt.lifecycle})};
      try{onSummary(summary);}catch{}
      receipt.cartId=null;receipt.latestCart=null;
      return ok;
    },
    verify(receipt,request,now){
      if(!receipts.has(receipt)||receipt.cleanupConfirmed!==true)return {eligible:false};
      const current=rawRequest(request),result=validateShippingCost({request:receipt.request,current,nowMs:now,lifecycle:receipt.lifecycle});
      receipt.lifecycle=null;receipts.delete(receipt);
      return {...result,expiresAt:request.startedAt+QUOTE_TTL_MS};
    }
  };
}


return {makeTransport};
})();
const {QuoteController}=(()=>{

class QuoteController {
  constructor({resolveZip, adapter, verify = () => null, onChange = () => {}, clock = () => Date.now(),
    setTimer = (fn,ms)=>setTimeout(fn,ms), clearTimer = id=>clearTimeout(id), ttlMs = 120000, timeoutMs = 15000} = {}) {
    if (typeof resolveZip !== 'function' || !Number.isSafeInteger(ttlMs) || ttlMs < 1 || ttlMs > 300000
      || !Number.isSafeInteger(timeoutMs) || timeoutMs < 1) throw new Error('Invalid quote configuration');
    Object.assign(this, {resolveZip, adapter, verify, onChange, clock, setTimer, clearTimer, ttlMs, timeoutMs});
    this.generation = 0; this.input = null; this.context = null; this.active = null; this.queued = false;
    this.connected = true; this.blocked = false; this.timer = null;
    this.state = Object.freeze({status:'idle', quoted:false, free:false, pending:false});
  }
  emit(status, extra = {}) {
    this.state = Object.freeze({status, quoted:false, free:false, pending:false, ...extra});
    if (this.connected) this.onChange(this.state);
  }
  contextAt(input, now) {
    if (!input || input.pendingVariant || typeof input.variantId !== 'string' || !/^[1-9]\d*$/.test(input.variantId)
      || !Number.isSafeInteger(input.quantity) || input.quantity < 1 || input.quantity > 2147483647 || !normalizeZip(input.zip)) return null;
    let destination;
    try { destination = this.resolveZip(input.zip, now); } catch { return null; }
    if (!destination || destination.zip !== normalizeZip(input.zip) || destination.countryCode !== 'US'
      || !isContiguousState(destination.state) || typeof destination.revision !== 'string' || !destination.revision
      || !Number.isSafeInteger(destination.expiresAt) || destination.expiresAt <= now) return null;
    return Object.freeze({variantId:input.variantId, quantity:input.quantity, zip:destination.zip, state:destination.state,
      countryCode:'US', resolverRevision:destination.revision, destinationExpiresAt:destination.expiresAt});
  }
  key(context) { return context ? JSON.stringify(context) : ''; }
  setContext(input) {
    const copy = input ? {variantId:input.variantId, quantity:input.quantity, zip:input.zip, pendingVariant:!!input.pendingVariant} : null;
    const next = this.contextAt(copy, this.clock());
    const same = this.key(next) === this.key(this.context) && JSON.stringify(copy) === JSON.stringify(this.input);
    this.input = copy;
    if (same) { this.refresh(); return; }
    this.context = next; this.invalidate(next ? 'ready' : 'unavailable');
  }
  invalidate(status = 'unavailable') {
    this.generation++; this.queued = false;
    this.clearTimer(this.timer); this.timer = null;
    this.emit(this.blocked ? 'cleanup-unconfirmed' : status);
  }
  refresh() {
    if (!this.connected) return;
    const next = this.contextAt(this.input, this.clock());
    if (this.key(next) !== this.key(this.context)) { this.context = next; this.invalidate(next ? 'ready' : 'unavailable'); }
    else if (this.state.quoted && this.clock() >= this.state.expiresAt) this.invalidate('expired');
  }
  check() {
    this.refresh();
    if (!this.connected || !this.context || this.blocked || !['create','read','cleanup'].every(k => typeof this.adapter?.[k] === 'function')) {
      this.emit(this.blocked ? 'cleanup-unconfirmed' : 'unavailable'); return Promise.resolve();
    }
    if (this.active) {
      // Never abort a cart mutation: its receipt is needed for cleanup.
      if (this.active.generation !== this.generation) { this.queued = true; this.emit('waiting', {pending:true}); }
      return this.active.done;
    }
    this.clearTimer(this.timer); this.timer = null;
    const request = Object.freeze({...this.context, generation:++this.generation, startedAt:this.clock()});
    const job = {generation:request.generation, timedOut:false}; this.active = job;
    this.emit('checking', {pending:true});
    job.done = this.run(job, request);
    return job.done;
  }
  async run(job, request) {
    let receipt, snapshot, cleanupConfirmed = false, operationError = false;
    const watchdog = this.setTimer(() => {
      job.timedOut = true;
      if (this.connected && request.generation === this.generation) this.emit('timeout');
    }, this.timeoutMs);
    try { receipt = await this.adapter.create(request); if (!receipt) throw new Error('Missing isolated cart receipt'); snapshot = await this.adapter.read(receipt, request); }
    catch { operationError = true; }
    finally {
      if (receipt) { try { cleanupConfirmed = await this.adapter.cleanup(receipt, request) === true; } catch {} }
      this.clearTimer(watchdog);
    }
    if (!cleanupConfirmed) { this.blocked = true; this.queued = false; this.invalidate('cleanup-unconfirmed'); }
    else if (this.connected && request.generation === this.generation) {
      const now = this.clock(), current = this.contextAt(this.input, now);
      let verdict = null;
      if (!job.timedOut && !operationError && this.key(current) === this.key(this.context) && now >= request.startedAt
        && now < request.startedAt + this.ttlMs) {
        try { verdict = this.verify(snapshot, request, now); } catch {}
      }
      const expiresAt = Math.min(request.startedAt + this.ttlMs, request.destinationExpiresAt, verdict?.expiresAt ?? NaN);
      if (verdict?.eligible === true && Number.isSafeInteger(verdict.costCents) && verdict.costCents >= 0
        && verdict.currency === 'USD' && Number.isSafeInteger(expiresAt) && expiresAt > now) {
        this.emit(verdict.costCents === 0 ? 'free' : 'quoted',
          {quoted:true, free:verdict.costCents === 0, costCents:verdict.costCents, currency:'USD', expiresAt});
        this.timer = this.setTimer(() => this.refresh(), expiresAt - now);
      } else this.emit(job.timedOut ? 'timeout' : 'unavailable');
    }
    this.active = null;
    if (this.connected && this.queued && !this.blocked) { this.queued = false; return this.check(); }
  }
  disconnect() {
    this.invalidate('disconnected'); this.connected = false;
    // An active operation retains only its isolated receipt until cleanup finishes.
    this.input = null; this.context = null;
  }
}


return {QuoteController};
})();
const {AutoQuoteScheduler}=(()=>{
class AutoQuoteScheduler {
  constructor({key,visible,state,run,clock=()=>Date.now(),setTimer=(fn,ms)=>setTimeout(fn,ms),clearTimer=id=>clearTimeout(id),debounceMs=450,cooldownMs=10000,maxAttempts=3}){
    Object.assign(this,{key,visible,state,run,clock,setTimer,clearTimer,debounceMs,cooldownMs,maxAttempts});
    this.records=new Map();this.timer=null;this.running=false;this.pending=null;this.destroyed=false;
  }
  request(manual=false){
    this.clearTimer(this.timer);this.timer=null;
    const key=this.key();if(this.destroyed||!key||!this.visible()){this.pending=null;return;}
    const record=this.records.get(key)||{attempts:0,lastStarted:-Infinity,stopped:false};
    if(!manual&&(this.state().quoted||record.stopped||record.attempts>=this.maxAttempts))return;
    this.pending={key,manual};if(this.running)return;
    const delay=manual?0:Math.max(this.debounceMs,record.lastStarted+this.cooldownMs-this.clock());
    this.timer=this.setTimer(()=>{this.timer=null;void this.drain();},delay);
  }
  async drain(){
    const next=this.pending;this.pending=null;
    if(this.destroyed||!next||!this.visible()||next.key!==this.key()||this.running)return;
    const record=this.records.get(next.key)||{attempts:0,lastStarted:-Infinity,stopped:false};
    if(next.manual){record.attempts=0;record.stopped=false;}
    if(record.stopped||record.attempts>=this.maxAttempts||!next.manual&&this.state().quoted)return;
    record.attempts++;record.lastStarted=this.clock();this.records.set(next.key,record);this.running=true;
    try{await this.run(next.manual);}catch{record.stopped=true;}
    finally{
      if(next.key===this.key()&&['unavailable','unsupported','timeout','cleanup-unconfirmed'].includes(this.state().status))record.stopped=true;
      this.running=false;
      if(!this.destroyed&&(this.pending||next.key!==this.key()))this.request(this.pending?.manual===true);
    }
  }
  pause(){this.clearTimer(this.timer);this.timer=null;this.pending=null;}
  destroy(){this.destroyed=true;this.pause();}
}


return {AutoQuoteScheduler};
})();
const {knownZip,rememberZip}=(()=>{
const SESSION_KEY='jdm-shipping-destination-v1',MAX_AGE_MS=30*60*1000;
const postalZip=value=>typeof value==='string'&&/^\d{5}(?:-\d{4})?$/.test(value.trim())?value.trim().slice(0,5):null;
const isUS=value=>['US','USA','UNITED STATES','UNITED STATES OF AMERICA'].includes(String(value||'').trim().toUpperCase());
function knownZip(element,win=element.ownerDocument.defaultView,now=Date.now()){
  const doc=element.ownerDocument;
  for(const calculator of doc.querySelectorAll('shipping-calculator')){
    if(calculator.querySelector('.js-zip-input')?.value?.trim()&&!isUS(calculator.querySelector('.js-shipping-country')?.value))return null;
  }
  try{const saved=JSON.parse(win.sessionStorage.getItem(SESSION_KEY));if(saved?.country==='US'&&Number.isSafeInteger(saved.at)&&saved.at<=now&&now-saved.at<MAX_AGE_MS&&postalZip(saved.zip))return{zip:postalZip(saved.zip),source:'session-input'};}catch{}
  const destinations=[];
  for(const calculator of doc.querySelectorAll('shipping-calculator')){
    if(calculator.dataset.jdmShippingZipConfirmed!=='true')continue;
    const zip=postalZip(calculator.querySelector('.js-zip-input')?.value),country=calculator.querySelector('.js-shipping-country')?.value;
    if(zip&&isUS(country))destinations.push(zip);
  }
  if(new Set(destinations).size===1)return{zip:destinations[0],source:'shipping-calculator'};
  if(new Set(destinations).size>1)return null;
  if(isUS(element.dataset.customerCountry)&&postalZip(element.dataset.customerZip))return{zip:postalZip(element.dataset.customerZip),source:'customer-default-address'};
  return null;
}
function rememberZip(value,win,now=Date.now()){
  try{const zip=postalZip(value);if(zip)win.sessionStorage.setItem(SESSION_KEY,JSON.stringify({zip,country:'US',at:now}));else win.sessionStorage.removeItem(SESSION_KEY);}catch{}
}


return {knownZip,rememberZip};
})();
const {bindQuoteComponent}=(()=>{

function bindQuoteComponent(element, {enabled = false, theme, resolveZip, adapter, verify,
  subscribe, events, prepare = async()=>{}, labels = {}, controllerOptions = {}, automatic=false, knownDestination=()=>null, rememberDestination=()=>{}, schedulerOptions={}} = {}) {
  element.hidden = true;
  const win = element.ownerDocument.defaultView, doc = element.ownerDocument;
  const scope = element.closest('product-info');
  const form = doc.getElementById(element.dataset.form);
  const zip = element.querySelector('[data-quote-zip]'), button = element.querySelector('[data-quote-check]');
  const badge = element.querySelector('[data-quote-free]'), status = element.querySelector('[data-quote-status]');
  const result = element.querySelector('[data-quote-result]'), paid = element.querySelector('[data-quote-paid]');
  const panel = element.querySelector('[data-quote-panel]'), change = element.querySelector('[data-quote-change]');
  const retry = element.querySelector('[data-quote-retry]'), summaryCopy = element.querySelector('[data-quote-summary-copy]');
  const validQuantity = quantity => Number.isSafeInteger(quantity) && quantity > 0 && quantity <= 2147483647;
  if (badge) badge.hidden = true;
  if (!enabled || !theme?.id || !['unpublished', 'main'].includes(theme?.role)
    || element.dataset.themeId !== String(theme?.id) || !scope || !form || !scope.contains(form)
    || !zip || !button || !badge || !status || !result || !paid || !panel || !change || !retry || !summaryCopy
    || typeof resolveZip !== 'function') return null;
  const abort = new win.AbortController(), off = [];
  let pendingVariant = false, destroyed = false, attempted = false, controller, scheduler, observer;
  let onScreen=!automatic,edited=false,adopted=false,automaticRun=false,editing=false;
  const visible=()=>!automatic||onScreen&&doc.visibilityState==='visible';
  const formId = form.getAttribute('id'); // form.id may be shadowed by input[name=id].
  function read() {
    const quantity = [...doc.querySelectorAll('[name="quantity"]')].find(n => n.form === form || n.getAttribute('form') === formId)
      || form.querySelector('[name="quantity"]');
    const id = form.querySelector('[name="id"]');
    const sellingPlan=[...doc.querySelectorAll('[name="selling_plan"]')].some(n=>(n.form===form||n.getAttribute('form')===formId)
      &&!n.disabled&&(!['radio','checkbox'].includes(n.type)||n.checked)&&String(n.value||'').trim());
    return {variantId:id?.value || '', quantity:quantity ? Number(quantity.value) : 1, zip:zip.value,
      pendingVariant:pendingVariant || sellingPlan || !!scope.pendingRequestUrl || !!id?.disabled};
  }
  const sync = () => { if (!destroyed) controller.setContext(read()); };
  const panelState = open => {
    const expanded = open || !controller?.state.quoted;
    panel.hidden = !expanded;
    change.hidden = !controller?.state.quoted;
    change.setAttribute('aria-expanded',String(expanded));
    change.textContent = zip.value.trim();
    change.setAttribute('aria-label',labels.change.replace('[zip]',zip.value.trim()));
  };
  const setStatus = text => {
    status.textContent = text; status.hidden = !text;
    summaryCopy.hidden = result.hidden && status.hidden && retry.hidden;
  };
  const render=state=>{
      badge.hidden = true;
      paid.hidden = true;
      result.hidden = true;
      retry.hidden = true;
      if (state.quoted && JSON.stringify(read()) !== JSON.stringify(controller.input)) { sync(); return; }
      button.disabled = state.pending || state.status === 'cleanup-unconfirmed';
      const input = read();
      const failure = ['unavailable','timeout','cleanup-unconfirmed'].includes(state.status);
      panelState(editing || !state.quoted);
      const message = state.pending ? labels.checking.replace('[zip]',input.zip.trim())
        : state.status === 'unsupported' ? labels.unsupported
        : state.status === 'cleanup-unconfirmed' ? labels.blocked
        : attempted && !/^\d{5}$/.test(input.zip.trim()) ? labels.invalid
        : !validQuantity(input.quantity) ? labels.quantity
        : attempted && failure && !input.pendingVariant ? labels.unavailable : '';
      zip.setAttribute('aria-invalid',String(attempted && (!/^\d{5}$/.test(input.zip.trim()) || state.status === 'unsupported')));
      retry.hidden = !(panel.hidden && attempted && failure && state.status !== 'cleanup-unconfirmed');
      button.textContent = attempted && failure ? labels.retry : labels.check;
      const destination=badge.querySelector('[data-quote-destination]');
      if (state.quoted) {
        if(destination)destination.textContent=controller.input.zip;
        paid.textContent=labels.paid.replace('[amount]',new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(state.costCents/100)).replace('[zip]',controller.input.zip);
        badge.hidden = state.free !== true;
        paid.hidden = state.free === true;
        result.hidden = false;
      }
      setStatus(message);
  };
  controller = new QuoteController({...controllerOptions, resolveZip, adapter, verify,
    onChange(state) {
      render(state);if(state.status==='expired')scheduler?.request();
    }});
  const on = (target, name, listener, capture = false) => target.addEventListener(name, listener, {signal:abort.signal, capture});
  const invalidateVariant = () => { pendingVariant = true; sync(); scheduler?.pause(); };
  on(scope, 'change', event => {
    if (event.target.closest?.('variant-selects') || event.target.matches?.('[name="id"]')) invalidateVariant();
  }, true);
  for (const event of ['input','change']) on(doc, event, e => {
    if(e.target===zip){edited=true;editing=true;rememberDestination(zip.value);}
    if (e.target === zip || e.target.matches?.('[name="quantity"], [name="selling_plan"]') && (e.target.form === form || e.target.getAttribute('form') === formId)){sync();scheduler?.request();}
    else if(automatic&&e.target.closest?.('shipping-calculator')){
      if(e.isTrusted)e.target.closest('shipping-calculator').dataset.jdmShippingZipConfirmed='true';
      edited=false;rememberDestination('');adoptKnown();sync();scheduler?.request();
    }
  }, true);
  let preparing=0;
  async function runCheck(manual=true){
    if(!visible())return;automaticRun=!manual;attempted=true;editing=false;sync();
    if(!/^\d{5}$/.test(zip.value.trim())){badge.hidden=true;paid.hidden=true;result.hidden=true;panelState(true);zip.setAttribute('aria-invalid','true');setStatus(labels.invalid);if(manual)zip.focus();return;}
    const snapshot=JSON.stringify(read()),generation=controller.generation,ticket=++preparing;
    badge.hidden=true;paid.hidden=true;result.hidden=true;
    setStatus(labels.checking.replace('[zip]',zip.value.trim()));button.disabled=true;
    try{await prepare();if(destroyed||!visible()||ticket!==preparing||generation!==controller.generation||snapshot!==JSON.stringify(read()))return;
      sync();
      if(!validQuantity(read().quantity)){controller.invalidate('unavailable');setStatus(labels.quantity);return;}
      if(!resolveZip(zip.value,Date.now())){controller.invalidate('unsupported');return;}
      await controller.check();
    }catch{controller.invalidate('unavailable');}
    finally{if(!destroyed)button.disabled=controller.state.pending||controller.blocked;}
  }
  const check=event=>{event.preventDefault();event.stopPropagation();if(scheduler&&validQuantity(read().quantity)&&/^\d{5}$/.test(zip.value.trim()))scheduler.request(true);else void runCheck(true);};
  on(button,'click',check);
  on(retry,'click',check);
  on(change,'click',event=>{event.preventDefault();editing=panel.hidden;panelState(editing);if(!panel.hidden){zip.focus();zip.select();}});
  on(panel,'keydown',event=>{if(event.key==='Escape'&&controller.state.quoted){event.preventDefault();editing=false;panelState(false);change.focus();}});
  on(zip,'keydown',event=>{if(event.key==='Enter')check(event);});
  on(win, 'pageshow', event => { if(event.persisted){controller.invalidate('expired');sync();} });
  function adoptKnown(){if(!edited){const known=knownDestination();if(known?.zip){zip.value=known.zip;adopted=true;}else if(adopted)zip.value='';}}
  function visibilityChanged(){
    if(!visible()){scheduler?.pause();preparing++;if(controller.active)controller.invalidate('paused');render(controller.state);}
    else{adoptKnown();sync();controller.refresh();render(controller.state);scheduler?.request();}
  }
  on(doc, 'visibilitychange',visibilityChanged);
  on(win,'pageshow',event=>{if(event.persisted){adoptKnown();sync();scheduler?.request();}});
  if (typeof subscribe === 'function' && events?.optionValueSelectionChange && events?.variantChange) {
    off.push(subscribe(events.optionValueSelectionChange, message => {
      if (scope.contains(message?.data?.event?.target)) invalidateVariant();
    }));
    off.push(subscribe(events.variantChange, message => {
      const data = message?.data;
      if (String(data?.sectionId) !== String(scope.sectionId || element.dataset.section)) return;
      const selected = form.querySelector('[name="id"]')?.value;
      pendingVariant = !data?.variant || String(data.variant.id) !== selected || data.variant.available !== true;
      sync();scheduler?.request();
    }));
  }
  if(automatic){
    scheduler=new AutoQuoteScheduler({...schedulerOptions,key:()=>{const r=read();return !r.pendingVariant&&validQuantity(r.quantity)&&/^[1-9]\d*$/.test(r.variantId)&&/^\d{5}$/.test(r.zip.trim())?JSON.stringify({variant:r.variantId,quantity:r.quantity,zip:r.zip.trim()}):null;},visible,state:()=>controller.state,run:runCheck});
    adoptKnown();
    if(typeof win.IntersectionObserver==='function'){observer=new win.IntersectionObserver(entries=>{onScreen=entries.some(e=>e.isIntersecting&&e.intersectionRatio>0);visibilityChanged();},{threshold:[0,.01]});observer.observe(element);}
  }
  sync(); element.hidden = false;
  return {controller, destroy() { destroyed = true; abort.abort(); off.forEach(fn => { if (typeof fn === 'function') fn(); });
    scheduler?.destroy();observer?.disconnect();controller.disconnect(); badge.hidden = true; paid.hidden=true;result.hidden=true; element.hidden = true; }};
}



return {bindQuoteComponent};
})();
const sources=new Map();
function loadSource(url){
  if(!sources.has(url))sources.set(url,fetch(url,{credentials:'omit',cache:'force-cache',redirect:'error'})
    .then(async response=>{if(!response.ok)throw Error('ZIP_UNAVAILABLE');return compactResolver(await response.json());})
    .catch(error=>{sources.delete(url);throw error;}));
  return sources.get(url);
}
if(!customElements.get('jdm-shipping-quote'))customElements.define('jdm-shipping-quote',class extends HTMLElement{
  connectedCallback(){
    if(this.binding)return;
    try{
      const labels=JSON.parse(this.querySelector('[data-quote-labels]').textContent),transport=makeTransport();
      let resolver=null;
      this.binding=bindQuoteComponent(this,{enabled:true,theme:window.Shopify?.theme,labels,automatic:true,
        knownDestination:()=>knownZip(this),rememberDestination:value=>rememberZip(value,window),
        resolveZip:(zip,now)=>resolver?.(zip,now)||null,
        prepare:async()=>{resolver=await loadSource(this.dataset.zipSource);},
        adapter:transport,verify:transport.verify,
        subscribe:typeof subscribe==='function'?subscribe:undefined,
        events:typeof PUB_SUB_EVENTS==='object'?PUB_SUB_EVENTS:undefined});
    }catch{this.hidden=true;}
  }
  disconnectedCallback(){this.binding?.destroy();this.binding=null;}
});

})();


